import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { Calendar as CalendarIcon, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

function daysBetween(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}
function toISO(d) {
  return d.toISOString().slice(0, 10);
}
function fromISO(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function BookingCalendar({ property }) {
  const { lang } = useI18n();
  const { format } = useCurrency();
  const [blocked, setBlocked] = useState([]);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(1);
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    api.get(`/properties/${property.property_id}/bookings`).then((r) => setBlocked(r.data || [])).catch(() => {});
  }, [property.property_id]);

  const blockedDates = useMemo(() => {
    const set = new Set();
    blocked.forEach((b) => {
      const ci = fromISO(b.check_in);
      const co = fromISO(b.check_out);
      for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
        set.add(toISO(d));
      }
    });
    return set;
  }, [blocked]);

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const startIdx = (first.getDay() + 6) % 7; // Monday = 0
    const arr = [];
    for (let i = 0; i < startIdx; i++) arr.push(null);
    for (let i = 1; i <= last.getDate(); i++) arr.push(new Date(month.getFullYear(), month.getMonth(), i));
    return arr;
  }, [month]);

  const nights = checkIn && checkOut ? daysBetween(checkIn, checkOut) : 0;
  const total = nights * property.price;

  const isBlocked = (d) => blockedDates.has(toISO(d));
  const isPast = (d) => d < today;

  const handleSelect = (d) => {
    if (!d || isPast(d) || isBlocked(d)) return;
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(d); setCheckOut(null); return;
    }
    if (d <= checkIn) { setCheckIn(d); return; }
    // Ensure no blocked date in range
    for (let x = new Date(checkIn); x < d; x.setDate(x.getDate() + 1)) {
      if (isBlocked(x)) { toast.error(lang === "fr" ? "Des dates sont indisponibles" : "Some dates unavailable"); return; }
    }
    setCheckOut(d);
  };

  const isInRange = (d) => checkIn && checkOut && d > checkIn && d < checkOut;

  const submit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut) { toast.error(lang === "fr" ? "Choisissez vos dates" : "Pick your dates"); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        property_id: property.property_id,
        check_in: toISO(checkIn), check_out: toISO(checkOut),
        guests_count: guests, ...form,
      });
      toast.success(lang === "fr" ? "Demande envoyée ! Nous revenons vers vous très vite." : "Request sent! We'll get back to you soon.");
      setCheckIn(null); setCheckOut(null);
      setForm({ guest_name: "", guest_email: "", guest_phone: "", message: "" });
      // refresh blocked dates
      const r = await api.get(`/properties/${property.property_id}/bookings`);
      setBlocked(r.data || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setSubmitting(false);
    }
  };

  const monthName = new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-US", { month: "long", year: "numeric" }).format(month);
  const weekDays = lang === "fr" ? ["L","M","M","J","V","S","D"] : ["M","T","W","T","F","S","S"];

  return (
    <div className="rounded-3xl bg-[#f5f5f7] p-6 sm:p-8" data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="font-medium text-[15px] capitalize">{monthName}</div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            className="w-8 h-8 rounded-full hover:bg-black/5 text-[#1d1d1f]"
            data-testid="cal-prev"
          >‹</button>
          <button
            type="button"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            className="w-8 h-8 rounded-full hover:bg-black/5 text-[#1d1d1f]"
            data-testid="cal-next"
          >›</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((d, i) => (
          <div key={i} className="text-[10px] uppercase tracking-wider text-[#86868b] text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const disabled = isPast(d) || isBlocked(d);
          const isCi = checkIn && toISO(d) === toISO(checkIn);
          const isCo = checkOut && toISO(d) === toISO(checkOut);
          const inRange = isInRange(d);
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => handleSelect(d)}
              className={`aspect-square rounded-full text-[13px] transition-all
                ${disabled ? "text-[#c7c7cc] line-through cursor-not-allowed" :
                  isCi || isCo ? "bg-[#1d1d1f] text-white font-semibold" :
                  inRange ? "bg-[#1d1d1f]/10 text-[#1d1d1f]" :
                  "hover:bg-black/5 text-[#1d1d1f]"}`}
              data-testid={`cal-day-${toISO(d)}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      {nights > 0 && (
        <div className="mt-6 pt-6 border-t border-black/10">
          <div className="flex items-center justify-between text-[14px]">
            <span className="text-[#86868b]">{format(property.price)} × {nights} {lang === "fr" ? "nuits" : "nights"}</span>
            <span className="font-medium">{format(total)}</span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3 mt-6" data-testid="booking-form">
        <div className="bg-white rounded-2xl p-3 flex items-center gap-3">
          <Users size={16} className="text-[#86868b]" />
          <select
            value={guests}
            onChange={(e) => setGuests(Number(e.target.value))}
            className="bg-transparent outline-none text-[14px] flex-1"
            data-testid="booking-guests"
          >
            {[1,2,3,4,5,6,7,8,9,10].map((n) => (
              <option key={n} value={n}>{n} {n === 1 ? (lang === "fr" ? "voyageur" : "guest") : (lang === "fr" ? "voyageurs" : "guests")}</option>
            ))}
          </select>
        </div>
        <input required value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder={lang === "fr" ? "Votre nom" : "Your name"} className="w-full bg-white rounded-2xl px-4 py-3 text-[14px] outline-none" data-testid="booking-name" />
        <input required type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} placeholder="Email" className="w-full bg-white rounded-2xl px-4 py-3 text-[14px] outline-none" data-testid="booking-email" />
        <input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} placeholder={lang === "fr" ? "Téléphone (optionnel)" : "Phone (optional)"} className="w-full bg-white rounded-2xl px-4 py-3 text-[14px] outline-none" data-testid="booking-phone" />
        <button
          type="submit"
          disabled={submitting || !checkIn || !checkOut}
          className="w-full btn-pill btn-primary text-[14px] flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="booking-submit"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {lang === "fr" ? "Demander la réservation" : "Request booking"}
        </button>
        <div className="text-[11px] text-[#86868b] text-center">
          {lang === "fr" ? "Vous ne serez pas débité maintenant." : "You won't be charged yet."}
        </div>
      </form>
    </div>
  );
}
